import { Component } from '@theme/component';

/**
 * How fast the desktop marquee scrolls, in pixels per second. Used to compute
 * the animation duration from the actual (filled) content width, so the pace
 * feels the same regardless of viewport size or how many copies were needed.
 */
const PIXELS_PER_SECOND = 70;

/**
 * Fills the desktop track with enough repeated copies of the real (editable)
 * announcement slides so it never shows a gap, duplicates the whole filled
 * track once for a seamless loop, and gives the mobile view its own copy of
 * the real slides (content_for 'blocks' can only render once per section, so
 * mobile can't get its own via Liquid).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper
 * @property {HTMLElement} content
 * @property {HTMLElement} mobileTrack
 *
 * @extends Component<Refs>
 */
class AnnouncementMarquee extends Component {
  requiredRefs = ['wrapper', 'content', 'mobileTrack'];

  connectedCallback() {
    super.connectedCallback();

    const { wrapper, content, mobileTrack } = this.refs;
    const realSlides = Array.from(content.children);

    if (realSlides.length === 0) return;

    for (const slide of realSlides) {
      mobileTrack.appendChild(slide.cloneNode(true));
    }

    // La marquesina existe para tapar un hueco: si las promesas entran enteras
    // en el ancho de la cinta, no hay hueco que tapar. Ahí se quedan quietas y
    // centradas (el CSS es el estado por defecto) y no se clona nada — leer
    // tres frases cortas que pasan de largo es peor que leerlas fijas.
    //
    // Se mide contra el ancho de la PISTA, no contra `window.innerWidth`: la
    // cinta puede ir a page-width o a full-width según el ajuste de la sección,
    // y con page-width la ventana es más ancha que el espacio real.
    //
    // La guarda `available > 0` importa: bajo 749px la pista de escritorio está
    // en `display: none`, así que mide 0 y `0 <= 0` daría "entra" por accidente.
    // Con la guarda, un teléfono cae al camino de siempre (clonar y animar) y
    // no cambia nada de lo que ya funcionaba ahí.
    const track = wrapper.parentElement;
    const available = track instanceof HTMLElement ? track.clientWidth : window.innerWidth;

    if (available > 0 && content.scrollWidth <= available) return;

    this.dataset.marquee = 'on';

    // El ancho se RE-LEE acá, después de encender la marquesina, y no se reusa la lectura de la
    // guarda de arriba. No es paranoia: `[data-marquee='on']` cambia el layout del contenido.
    // Con la cinta quieta, `sections/header-announcements.liquid` pinta un separador `·` con su
    // `margin-inline-end` entre promesa y promesa; al encenderla, esa regla deja de aplicar y el
    // set se vuelve más angosto. Medir antes daría un set más ancho que el real, la cuenta de
    // clones saldría corta y la cinta quedaría con un hueco al dar la vuelta.
    //
    // Son dos lecturas fijas, no una: sigue siendo el punto clave frente al bucle que había antes.
    const setWidth = content.scrollWidth;

    // ANTES: un `while` que releía `content.scrollWidth` en cada vuelta con `appendChild` en el
    // medio. Leer una geometría después de escribir en el DOM obliga al navegador a rehacer el
    // layout ahí mismo, sincrónicamente: eran hasta 20 reflows forzados, en el `connectedCallback`
    // de un elemento que vive arriba de todo en TODAS las páginas. Y cada `appendChild` es además
    // una mutación del documento, que despertaba a los observadores globales del tema.
    //
    // La cuenta es la misma, pero resuelta con aritmética en vez de con prueba y error: con un set
    // de `setWidth` px hacen falta `objetivo / setWidth` sets para cubrir el objetivo, y ya hay uno
    // puesto. `ceil` redondea para arriba y los `gap` entre diapositivas sólo suman ancho, así que
    // el resultado nunca se queda corto — que es el único error que se vería (una cinta con hueco).
    //
    // Se arman todos los clones en un `DocumentFragment` y se insertan de una: una sola mutación
    // del DOM y un solo layout, en lugar de uno por clon.
    // `setWidth === 0` significa que la pista de escritorio está oculta, y eso pasa en TODOS los
    // teléfonos: `.announcement-bar__slides--desktop` va a `display: none` bajo 749px. El bucle
    // viejo, al no poder medir, se iba al tope y metía 20 sets de clones dentro de un contenedor
    // invisible — invisibles también ellos, y encima 20 tandas de mutaciones del DOM que despertaban
    // a los observadores globales. Acá no se clona nada: el resultado en pantalla es el mismo
    // (`--announcement-speed` sale 0s en los dos casos) y el teléfono se ahorra el trabajo entero.
    // La cinta que sí se ve en móvil es `mobileTrack`, que se llena más arriba y por otro camino.
    const objetivo = window.innerWidth * 2;
    const setsFaltantes = setWidth > 0 ? Math.min(20, Math.ceil(objetivo / setWidth) - 1) : 0;

    if (setsFaltantes > 0) {
      const lote = document.createDocumentFragment();
      for (let i = 0; i < setsFaltantes; i++) {
        for (const slide of realSlides) {
          const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
          clone.setAttribute('aria-hidden', 'true');
          lote.appendChild(clone);
        }
      }
      content.appendChild(lote);
    }

    const duration = content.scrollWidth / PIXELS_PER_SECOND;
    wrapper.style.setProperty('--announcement-speed', `${duration}s`);

    const duplicate = /** @type {HTMLElement} */ (content.cloneNode(true));
    duplicate.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(duplicate);
  }
}

if (!customElements.get('announcement-marquee-component')) {
  customElements.define('announcement-marquee-component', AnnouncementMarquee);
}
